import type { DashboardAction, TextKey, Translated } from "@repo/shared/types";
import { AlertCircle, Clock, FileX, type LucideIcon, PenLine } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { DashboardTarget } from "@/lib/api/dashboard";
import { useDashboardWidget } from "@/lib/hooks/use-dashboard";
import { useT } from "@/lib/i18n/use-t";
import { useDashboardFormat } from "../use-dashboard-format";
import { T } from "./action-items-widget.i18n";
import { ListRow } from "./list-row";
import type { Tone } from "./tone";
import { WidgetCard } from "./widget-card";
import { WidgetEmpty } from "./widget-empty";

type Label = TextKey<Translated<typeof T>>;

const ACTION: Record<
  DashboardAction["type"],
  {
    icon: LucideIcon;
    tone: Tone;
    badge: Label;
    badgeVariant: "secondary" | "destructive";
  }
> = {
  signature: { icon: PenLine, tone: "warning", badge: "signatureBadge", badgeVariant: "secondary" },
  stale_flow: { icon: Clock, tone: "info", badge: "inactiveBadge", badgeVariant: "secondary" },
  failed_pdf: {
    icon: FileX,
    tone: "destructive",
    badge: "failedBadge",
    badgeVariant: "destructive",
  },
};

/** Today's action items, plus signing links about to expire marked on their own row. */
export function ActionItemsWidget({ target }: { target: DashboardTarget }) {
  const { data, isLoading, error } = useDashboardWidget("action_items", target);
  const t = useT(T);
  const { count, relative } = useDashboardFormat();

  return (
    <WidgetCard
      icon={AlertCircle}
      title={t.actionItems}
      description={t.needsAttention}
      badge={data && data.length > 0 ? count(data.length) : undefined}
      isLoading={isLoading}
      error={error}
    >
      {data &&
        (data.length === 0 ? (
          <WidgetEmpty>{t.noPendingActions}</WidgetEmpty>
        ) : (
          <div className="space-y-3">
            {data.map((action) => {
              const config = ACTION[action.type];
              return (
                <ListRow
                  key={`${action.type}-${action.id}`}
                  icon={config.icon}
                  tone={action.expiresAt ? "destructive" : config.tone}
                  title={action.title}
                  badge={<Badge variant={config.badgeVariant}>{t[config.badge]}</Badge>}
                  detail={action.description}
                  meta={
                    action.expiresAt ? (
                      <span className="font-medium text-destructive">
                        {t.expires(relative(action.expiresAt))}
                      </span>
                    ) : (
                      relative(action.createdAt)
                    )
                  }
                  to={action.link}
                />
              );
            })}
          </div>
        ))}
    </WidgetCard>
  );
}
